/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { GitMerge, Layers, Loader2, Pencil, ScanLine } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const CONSOL_RE = /consolidated (\d+) duplicate lines/i;
const err = (e) => formatErr(e?.response?.data?.detail);
const money = (v) => (v ? Number(v).toFixed(2) : "—");

/** How many original document lines sit behind this line (0 = not merged). */
export function mergedCount(item) {
  if ((item.source_lines || []).length > 1) return item.source_lines.length;
  const m = CONSOL_RE.exec(item.notes || "");
  return m ? Number(m[1]) : 0;
}

/** "Merged from N lines": view the originals and split them back. */
export function MergedLinesPanel({ item, baseUrl, onDone, testPrefix }) {
  const n = mergedCount(item);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || data) return;
    api.get(`${baseUrl}/source-lines`).then((r) => setData(r.data)).catch((e) => toast.error(err(e)));
  }, [open, data, baseUrl]);
  if (n < 2) return null;
  const unmerge = async () => {
    if (data && !data.qty_matches && !window.confirm("The original lines don't add up to this line's quantity. Split anyway? Check the quantities afterwards.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`${baseUrl}/unmerge`);
      toast.success(`Split back into ${r.created_ids.length + 1} lines`);
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-500/10 text-amber-200 inline-flex items-center gap-1" data-testid={`${testPrefix}-merged-toggle-${item.id}`}>
        <Layers className="w-3 h-3" /> Merged from {n} lines {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="mt-1 rounded border border-white/10 bg-black/30 p-2 space-y-1" data-testid={`${testPrefix}-merged-panel-${item.id}`}>
          {!data ? <Loader2 className="w-3 h-3 animate-spin text-cyan-300" /> : (
            <>
              {data.lines.map((l, i) => (
                <div key={i} className="text-[11px] text-[#dde3ec] flex gap-2" data-testid={`${testPrefix}-merged-line-${item.id}-${i}`}>
                  <span className="flex-1 break-words">{l.name}{l.sku && <span className="text-[#647087] font-mono"> · {l.sku}</span>}</span>
                  <span className="font-mono">× {l.qty}</span>
                  {l.unit_cost ? <span className="font-mono text-[#a0aabf]">@ {money(l.unit_cost)}</span> : null}
                </div>
              ))}
              {data.lines.length < 2 && <div className="text-[11px] text-amber-200">The original lines couldn't be found on the saved scan. Use Rescan document instead.</div>}
              {!data.stored && data.lines.length >= 2 && !data.qty_matches && (
                <div className="text-[10px] text-amber-200">Rebuilt from the saved scan. The quantities don't add up exactly, so check them after splitting.</div>
              )}
              {data.locked ? (
                <div className="text-[10px] text-[#a0aabf]">Already received or picked, so this line stays as is.</div>
              ) : data.lines.length >= 2 && (
                <button type="button" disabled={busy} onClick={unmerge} className="hv-btn hv-btn-primary text-[11px] mt-1" data-testid={`${testPrefix}-unmerge-${item.id}`}>
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Split back into {data.lines.length} lines
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** Pencil: correct a line's description and code by hand. */
export function EditLineButton({ item, url, onDone, testPrefix }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(item.name || "");
  const [sku, setSku] = useState(item.sku || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(url, { name, sku });
      toast.success("Line updated");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) { setName(item.name || ""); setSku(item.sku || ""); } }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="text-[#a0aabf] hover:text-cyan-300" title="Edit description and code" data-testid={`${testPrefix}-edit-${item.id}`}>
          <Pencil className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2" onClick={(e) => e.stopPropagation()}>
        <label className="block text-[10px] uppercase text-[#647087]">Description
          <textarea value={name} onChange={(e) => setName(e.target.value)} rows={2} className="hv-input w-full text-xs mt-0.5" data-testid={`${testPrefix}-edit-name-${item.id}`} />
        </label>
        <label className="block text-[10px] uppercase text-[#647087]">Code / SKU
          <input value={sku} onChange={(e) => setSku(e.target.value)} className="hv-input w-full text-xs mt-0.5 font-mono" data-testid={`${testPrefix}-edit-sku-${item.id}`} />
        </label>
        <button type="button" disabled={busy || !name.trim()} onClick={save} className="hv-btn hv-btn-primary text-xs w-full justify-center" data-testid={`${testPrefix}-edit-save-${item.id}`}>Save</button>
      </PopoverContent>
    </Popover>
  );
}

/** Manual merge for procurement lines: pick another unreceived line on the same order. */
export function MergeIntoButton({ item, siblings, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const others = (siblings || []).filter((s) => s.id !== item.id && !(Number(s.qty_delivered) > 0));
  if (Number(item.qty_delivered) > 0 || !others.length) return null;
  const merge = async (target) => {
    if (!window.confirm(`Merge "${item.name}" into "${target.name}"? Quantities are added together. You can split them back later.`)) return;
    setBusy(true);
    try {
      await api.post(`/procurement/items/${item.id}/merge-into`, { target_id: target.id });
      toast.success("Lines merged");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="text-[#a0aabf] hover:text-cyan-300" title="Merge with another line" data-testid={`procurement-item-merge-btn-${item.id}`}>
          <GitMerge className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-72 overflow-y-auto p-1" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase text-[#647087] px-2 py-1">Merge this line into…</div>
        {others.map((s) => (
          <button key={s.id} type="button" disabled={busy} onClick={() => merge(s)} className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-cyan-500/10" data-testid={`procurement-item-merge-target-${item.id}-${s.id}`}>
            <div className="text-[#dde3ec]">{s.name}</div>
            <div className="text-[10px] text-[#647087] font-mono">{s.sku} · qty {s.qty_ordered}{s.source_doc_number ? ` · ${s.source_doc_number}` : ""}{s.expected_delivery_date ? ` · ETA ${s.expected_delivery_date}` : ""}</div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const TYPE = {
  change: ["Will change", "text-amber-200"], add: ["Will be added", "text-emerald-300"],
  remove: ["Will be removed", "text-rose-300"], locked: ["Received, won't change", "text-[#a0aabf]"],
  same: ["No change", "text-[#647087]"],
};
const Vals = ({ v }) => v ? <span>{v.name}{v.sku ? <span className="font-mono text-[#647087]"> · {v.sku}</span> : null} × {v.qty}{v.unit_cost ? ` @ ${money(v.unit_cost)}` : ""}</span> : null;

/** Re-read the PO document, preview what changes, then apply. Received lines never change. */
export function RescanDialog({ tracker, docs, onClose, onApplied }) {
  const ids = tracker.source_supplier_doc_ids || [];
  const [did, setDid] = useState(ids.length === 1 ? ids[0] : "");
  const [phase, setPhase] = useState("pick");
  const [diff, setDiff] = useState(null);
  const label = (id) => docs.find((d) => d.id === id)?.document_number || id.slice(0, 12);
  const start = async (id) => {
    setDid(id); setPhase("reading"); setDiff(null);
    try {
      await api.post(`/suppliers/${tracker.supplier_id}/documents/${id}/rescan-ocr`);
      for (let i = 0; i < 90; i += 1) {
        await new Promise((r) => setTimeout(r, 4000));
        const { data } = await api.get(`/suppliers/${tracker.supplier_id}/documents/${id}`);
        if (data.ocr_status === "error") throw new Error(data.ocr_error || "Reading the document failed.");
        if (data.ocr_status === "ready") break;
      }
      const { data } = await api.get(`/procurement/trackers/${tracker.id}/rescan-diff`, { params: { supplier_doc_id: id } });
      setDiff(data); setPhase("review");
    } catch (e) { toast.error(e?.response ? err(e) : e.message); setPhase("pick"); }
  };
  useEffect(() => { if (ids.length === 1) start(ids[0]); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const apply = async () => {
    setPhase("applying");
    try {
      const { data } = await api.post(`/procurement/trackers/${tracker.id}/rescan-apply`, { supplier_doc_id: did });
      toast.success(`Rescan applied: ${data.change} changed, ${data.add} added, ${data.remove} removed`);
      onApplied?.(); onClose();
    } catch (e) { toast.error(err(e)); setPhase("review"); }
  };
  const changes = diff ? diff.rows.filter((r) => ["change", "add", "remove"].includes(r.type)).length : 0;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="procurement-rescan-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ScanLine className="w-4 h-4" /> Rescan document</DialogTitle></DialogHeader>
        {phase === "pick" && (
          <div className="space-y-2">
            <div className="text-xs text-[#a0aabf]">Pick the PO to read again. You'll see every change before anything is saved, and received lines never change.</div>
            {ids.map((id) => (
              <button key={id} type="button" onClick={() => start(id)} className="hv-btn hv-btn-ghost text-xs w-full justify-start" data-testid={`procurement-rescan-doc-${id}`}>{label(id)}</button>
            ))}
          </div>
        )}
        {phase === "reading" && <div className="text-xs text-[#a0aabf] flex items-center gap-2 py-6" data-testid="procurement-rescan-reading"><Loader2 className="w-4 h-4 animate-spin" /> Reading {label(did)} again… this can take a minute.</div>}
        {(phase === "review" || phase === "applying") && diff && (
          <div className="space-y-2">
            <div className="text-xs text-[#a0aabf]" data-testid="procurement-rescan-summary">
              {label(did)}: {diff.counts.change} change, {diff.counts.add} new, {diff.counts.remove} removed, {diff.counts.locked} received (kept), {diff.counts.same} unchanged.
            </div>
            <div className="text-[10px] text-amber-200">Lines that aren't received will follow the new scan exactly. Any hand merges or edits on them are replaced.</div>
            <div className="space-y-1">
              {diff.rows.filter((r) => r.type !== "same").map((r, i) => (
                <div key={i} className="rounded border border-white/10 p-2 text-[11px]" data-testid={`procurement-rescan-row-${i}`}>
                  <div className={`uppercase text-[10px] font-bold ${TYPE[r.type][1]}`}>{TYPE[r.type][0]}</div>
                  {r.before && <div className={r.type === "change" || r.type === "remove" ? "line-through text-[#647087]" : "text-[#dde3ec]"}><Vals v={r.before} /></div>}
                  {r.after && r.type !== "locked" && <div className="text-[#dde3ec]"><Vals v={r.after} /></div>}
                </div>
              ))}
              {changes === 0 && <div className="text-xs text-emerald-300">The new scan matches the current lines. Nothing to change.</div>}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="procurement-rescan-cancel">Cancel</button>
              <button type="button" disabled={!changes || phase === "applying"} onClick={apply} className="hv-btn hv-btn-primary text-xs" data-testid="procurement-rescan-apply">
                {phase === "applying" ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Apply {changes} change{changes === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
